import { createSignal } from "solid-js";
import {
  CONSENT_DEBUG_QUERY_PARAM,
  CONSENT_STORAGE_KEY,
  CONSENT_VERSION,
} from "../constants/consent";

/**
 * The client-side proof of consent: which non-essential category the user chose,
 * when, and against which policy version. This is the ONLY consent record — there
 * is no server ledger (SPEC Non-Goals). No PostHog import lives in this module.
 */
export type ConsentRecord = {
  analytics: boolean;
  timestamp: string;
  version: string;
};

function isConsentRecord(value: unknown): value is ConsentRecord {
  return (
    typeof value === "object" &&
    value !== null &&
    "analytics" in value &&
    typeof (value as Record<string, unknown>).analytics === "boolean" &&
    "timestamp" in value &&
    typeof (value as Record<string, unknown>).timestamp === "string" &&
    "version" in value &&
    typeof (value as Record<string, unknown>).version === "string"
  );
}

/**
 * Reads the persisted consent record (sync). Returns `null` — treated as "no
 * choice", i.e. opt-out + banner — when the value is absent, unreadable,
 * malformed, or stored against a different `CONSENT_VERSION`. A corrupt or
 * ambiguous value is NEVER interpreted as consent (AC 13, fail-safe).
 */
export function getStoredConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isConsentRecord(parsed)) return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

const [consent, setConsent] = createSignal<ConsentRecord | null>(
  getStoredConsent(),
);

/** Reactive accessor for the current consent record (source of truth for the UI). */
export { consent };

/**
 * Re-hydrates the in-memory signal from storage. Used by the boot wiring and
 * available for any caller that needs to realign the signal with what persisted.
 */
export function syncConsentFromStorage(): void {
  setConsent(getStoredConsent());
}

/**
 * Persists the user's analytics choice and updates the signal.
 *
 * If the write fails (private mode, quota, storage disabled) the signal is still
 * updated, so the choice holds for the current session (AC 13); nothing was
 * persisted, so the banner will re-appear on the next visit — the conservative
 * fail-safe. Returns the record that now represents the current choice.
 */
export function saveConsent(analytics: boolean): ConsentRecord {
  const record: ConsentRecord = {
    analytics,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch {
      // ignore quota / private mode — the choice still holds in memory this session
    }
  }
  setConsent(record);
  return record;
}

/** True when a valid, current-version choice is on record. */
export function hasValidChoice(): boolean {
  return consent() !== null;
}

/**
 * Reads the QA override query param (`?cookie_banner=1`). Not persisted — it only
 * forces the CMP surfaces on for the current session (D11).
 */
export function getDebugOverride(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      new URLSearchParams(window.location.search).get(
        CONSENT_DEBUG_QUERY_PARAM,
      ) === "1"
    );
  } catch {
    return false;
  }
}

/**
 * Whether the CMP surfaces (banner, "Preferenze cookie") should exist at all:
 * only when analytics could actually run here — env enabled, or the QA override
 * is present. Hides the re-entry point where there is nothing to gate (A4).
 */
export function isTrackingAvailable(
  envEnabled: boolean,
  forceOverride: boolean,
): boolean {
  return envEnabled || forceOverride;
}

/**
 * Pure banner-visibility predicate: shown only when tracking could run
 * (`isTrackingAvailable`) AND the user has not yet made a valid choice.
 */
export function shouldShowBanner(
  envEnabled: boolean,
  hasChoice: boolean,
  forceOverride: boolean,
): boolean {
  if (hasChoice) return false;
  return isTrackingAvailable(envEnabled, forceOverride);
}

/**
 * The effective capture rule: non-essential capture is allowed only when the
 * env kill-switch is on AND the user explicitly accepted analytics.
 */
export function isCaptureAllowed(envEnabled: boolean): boolean {
  return envEnabled && consent()?.analytics === true;
}

// --- Preferences panel UI state ------------------------------------------------
// Shared, module-level so any surface (banner "Personalizza", footer "Preferenze
// cookie") can open the single panel instance mounted at the router root.

const [preferencesOpen, setPreferencesOpen] = createSignal(false);

/** Reactive accessor: is the "Personalizza" preferences panel open? */
export { preferencesOpen };

/** Opens the preferences panel (from the banner or the footer re-entry point). */
export function openPreferences(): void {
  setPreferencesOpen(true);
}

/** Closes the preferences panel, returning to whatever is underneath (banner/page). */
export function closePreferences(): void {
  setPreferencesOpen(false);
}
